import React, { useCallback, useEffect, useState } from "react";
import { UserCheck, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InactiveListCard } from "@/components/admin/InactiveListCard";
import { InactiveAutoDeleteCard } from "@/components/admin/InactiveAutoDeleteCard";
import { fmtInt } from "@/lib/activityFmt";

// Lot 765 — Admin › Utilisateurs › « Connectés » : membres ayant ouvert l'app ou le site (dernière activité) sur
// 1 · 7 · 30 · 90 jours, liste des inactifs depuis N jours (suppression manuelle), suppression automatique réglable.
const WINDOWS = [
  { key: "day", label: "Aujourd'hui", hint: "dernières 24 h" },
  { key: "week", label: "7 jours", hint: "semaine" },
  { key: "month", label: "30 jours", hint: "mois" },
  { key: "days90", label: "90 jours", hint: "trimestre" },
];
const StatCard = ({ w, data }) => {
  const win = data?.windows?.[w.key];
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid={`active-stat-${w.key}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{w.label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums" data-testid={`active-stat-${w.key}-value`}>{fmtInt(win?.count)}</div>
      <div className="text-xs mt-0.5 tabular-nums" style={{ color: "var(--yalola-text-muted)" }}>
        {win ? `${win.pct} % des membres` : "…"} · {w.hint}
      </div>
    </div>
  );
};

export const ActiveUsersPanel = ({ isSuper }) => {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/admin/activity/stats"), api.get("/admin/activity/config")]);
      setStats(s.data); setConfig(c.data);
    } catch { /* toast inutile : les cartes affichent « … » */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const refreshStats = () => api.get("/admin/activity/stats").then(({ data }) => setStats(data)).catch(() => {});
  return (
    <div className="space-y-3" data-testid="active-users-panel">
      <div className="yalola-card p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <UserCheck size={18} style={{ color: "var(--yalola-primary)" }} />
            <div>
              <div className="font-bold font-display">Membres connectés</div>
              <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Un membre compte comme connecté dès qu'il ouvre l'app ou le site (dernière activité).</div>
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={load} disabled={loading} data-testid="active-refresh">
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />} Actualiser
          </Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {WINDOWS.map((w) => <StatCard key={w.key} w={w} data={stats} />)}
        </div>
        <div className="text-xs mt-3 flex flex-wrap gap-x-3 gap-y-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="active-footer">
          <span><b data-testid="active-total">{fmtInt(stats?.total_members)}</b> membres</span>
          <span>· <b data-testid="active-never">{fmtInt(stats?.never_seen)}</b> jamais revus depuis l'inscription</span>
          <span>· <b data-testid="active-inactive30">{fmtInt(stats?.inactive_30)}</b> inactifs depuis 30 jours ou plus</span>
        </div>
      </div>
      {config && <InactiveListCard isSuper={isSuper} config={config} onConfig={setConfig} onDeleted={refreshStats} />}
      {config && <InactiveAutoDeleteCard isSuper={isSuper} config={config} onConfig={setConfig} />}
    </div>
  );
};
